import { round_cases } from "./quantity_basis"
import type {
  ColumnConfig,
  MetricMode,
  SalesDataPoint,
  SalesHierarchyNode,
} from "../types"

/** Keys aligned with retailer CSV ingest `detail_metrics` JSON. */
export const FORECAST_DETAIL = {
  consumption_gross_sales: "consumption_gross_sales",
  gross_sales_delivered: "gross_sales_delivered",
  forecasted_consumption_cases: "forecasted_consumption_cases",
  forecasted_delivery_cases: "forecasted_delivery_cases",
  forecasted_consumption_units: "forecasted_consumption_units",
  seasonality_baseline_units: "seasonality_adjusted_baseline_consumption_units",
  incremental_promo_units: "incremental_promotion_consumption_units",
  total_promotion_lift: "total_promotion_lift",
} as const

export interface ForecastMeasureAvailability {
  has_detail: boolean
  consumption_dollars: boolean
  delivered_dollars: boolean
  consumption_cases: boolean
  delivery_cases: boolean
  forecast_units: boolean
  baseline_units: boolean
  incremental_promo_units: boolean
  promotion_lift: boolean
}

function detail_num(m: Record<string, number> | undefined, k: string): number | undefined {
  if (!m) return undefined
  const v = m[k]
  return typeof v === "number" && Number.isFinite(v) ? v : undefined
}

/** Parse JSON detail from API into finite numeric fields only. */
export function parse_detail_metrics(raw: unknown): Record<string, number> | undefined {
  if (raw == null || typeof raw !== "object") {
    return undefined
  }
  const out: Record<string, number> = {}
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    const n = Number(v)
    if (Number.isFinite(n)) {
      out[k] = n
    }
  }
  return Object.keys(out).length ? out : undefined
}

function has_positive_value(detail: Record<string, number> | undefined, key: string): boolean {
  const v = detail_num(detail, key)
  return v !== undefined && v !== 0
}

/** Scan leaf sales_data for optional retailer breakdown keys (non-zero = available). */
export function scan_forecast_measure_availability(roots: unknown[]): ForecastMeasureAvailability {
  const empty: ForecastMeasureAvailability = {
    has_detail: false,
    consumption_dollars: false,
    delivered_dollars: false,
    consumption_cases: false,
    delivery_cases: false,
    forecast_units: false,
    baseline_units: false,
    incremental_promo_units: false,
    promotion_lift: false,
  }

  function walk(node: any): void {
    const children = node?.children as unknown[] | undefined
    if (Array.isArray(children) && children.length > 0) {
      children.forEach(walk)
      return
    }
    const sales = node?.sales_data as SalesDataPoint[] | undefined
    if (!Array.isArray(sales)) return
    for (const dp of sales) {
      const detail = dp.metrics?.detail
      if (detail && Object.keys(detail).length > 0) {
        empty.has_detail = true
        if (has_positive_value(detail, FORECAST_DETAIL.consumption_gross_sales)) {
          empty.consumption_dollars = true
        }
        if (has_positive_value(detail, FORECAST_DETAIL.gross_sales_delivered)) {
          empty.delivered_dollars = true
        }
        if (has_positive_value(detail, FORECAST_DETAIL.forecasted_consumption_cases)) {
          empty.consumption_cases = true
        }
        if (has_positive_value(detail, FORECAST_DETAIL.forecasted_delivery_cases)) {
          empty.delivery_cases = true
        }
        if (has_positive_value(detail, FORECAST_DETAIL.forecasted_consumption_units)) {
          empty.forecast_units = true
        }
        if (has_positive_value(detail, FORECAST_DETAIL.seasonality_baseline_units)) {
          empty.baseline_units = true
        }
        if (has_positive_value(detail, FORECAST_DETAIL.incremental_promo_units)) {
          empty.incremental_promo_units = true
        }
        if (has_positive_value(detail, FORECAST_DETAIL.total_promotion_lift)) {
          empty.promotion_lift = true
        }
      }
    }
  }

  for (const r of roots) {
    walk(r)
  }
  return empty
}

function basis_dollars(cfg: ColumnConfig): string {
  return cfg.forecast_dollar_basis ?? FORECAST_DETAIL.consumption_gross_sales
}

function basis_cases(cfg: ColumnConfig): string {
  return cfg.forecast_case_basis ?? FORECAST_DETAIL.forecasted_consumption_cases
}

function basis_units(cfg: ColumnConfig): string {
  return cfg.forecast_unit_basis ?? FORECAST_DETAIL.forecasted_consumption_units
}

/** True when the unified slice is **Delivered** (no delivered-units key in detail yet — units row shows **—**). */
export function is_delivered_detail_channel(cfg: ColumnConfig): boolean {
  return basis_dollars(cfg) === FORECAST_DETAIL.gross_sales_delivered
}

/** Resolved numbers for table cells and rollups (canonical metrics when no detail). */
export function resolve_display_metrics(dp: SalesDataPoint, cfg: ColumnConfig): {
  volume_dollars: number
  cases_sold: number
  units_sold: number | null
} {
  const m = dp.metrics
  const detail = m?.detail
  const vol_base = Number(m?.volume_dollars ?? 0)
  const cases_base = Number(m?.cases_sold ?? 0)
  const units_base = m?.units_sold
  if (!detail) {
    return {
      volume_dollars: vol_base,
      cases_sold: cases_base,
      units_sold: is_delivered_detail_channel(cfg) ? null : units_base ?? null,
    }
  }
  const dk = basis_dollars(cfg)
  const ck = basis_cases(cfg)
  const uk = basis_units(cfg)
  const volume_dollars = detail_num(detail, dk) ?? vol_base
  const cases_sold = round_cases(detail_num(detail, ck) ?? cases_base)
  if (is_delivered_detail_channel(cfg)) {
    return {
      volume_dollars,
      cases_sold,
      units_sold: null,
    }
  }
  return {
    volume_dollars,
    cases_sold,
    units_sold:
      detail_num(detail, uk) ??
      (units_base !== undefined && units_base !== null ? Number(units_base) : null),
  }
}

/** Row kind for per-cell primary selection. Plan rows are `forecast` (explicit kind, or unset with `is_forecast`). */
function primary_row_kind(dp: SalesDataPoint): "actual" | "forecast" | "other" {
  const kind = (dp as { source_kind?: string }).source_kind
  if (kind === "actual") return "actual"
  if (kind === "forecast") return "forecast"
  if (kind == null) {
    return (dp as { is_actual?: boolean }).is_actual ? "actual" : "forecast"
  }
  return "other"
}

/**
 * When several rows share a period on one node (e.g. promomash writes `forecast`
 * AND `volume_based_estimate` rows per week), the plan `forecast` row is the cell's
 * primary: it is what the grid displays, what totals count, and what edits target.
 * Other non-actual kinds are shadowed for that period (still saved, just not shown
 * or double-counted). Actual rows are never shadowed — the actual-wins rule applies
 * downstream.
 */
export function select_primary_sales_rows<T extends SalesDataPoint>(salesData: T[] | undefined): T[] {
  if (!Array.isArray(salesData)) {
    return []
  }
  const periods_with_forecast = new Set<string>()
  for (const dp of salesData) {
    const pk = dp.period?.period_key
    if (pk && primary_row_kind(dp) === "forecast") {
      periods_with_forecast.add(pk)
    }
  }
  if (periods_with_forecast.size === 0) {
    return salesData
  }
  return salesData.filter((dp) => {
    const pk = dp.period?.period_key
    if (!pk) return true
    const kind = primary_row_kind(dp)
    if (kind === "actual" || kind === "forecast") return true
    return !periods_with_forecast.has(pk)
  })
}

export function get_display_volume(dp: SalesDataPoint, cfg: ColumnConfig): number {
  return resolve_display_metrics(dp, cfg).volume_dollars
}

export function get_display_cases(dp: SalesDataPoint, cfg: ColumnConfig): number {
  return resolve_display_metrics(dp, cfg).cases_sold
}

export function get_display_units(dp: SalesDataPoint, cfg: ColumnConfig): number | null {
  return resolve_display_metrics(dp, cfg).units_sold
}

/**
 * Imputed promo **revenue** lift for one cell: incremental promo units × (cell revenue ÷ display units).
 * Returns null when inputs do not support a positive lift.
 */
export function imputed_promo_lift_revenue(
  volume_dollars: number,
  display_units: number | null | undefined,
  promo_incremental_units: number,
): number | null {
  const u = display_units == null ? NaN : Number(display_units)
  if (!Number.isFinite(volume_dollars) || !Number.isFinite(u) || u <= 0) {
    return null
  }
  if (!Number.isFinite(promo_incremental_units) || promo_incremental_units <= 0) {
    return null
  }
  const vol_cents = Math.round(volume_dollars * 100)
  const lift_cents = Math.round((promo_incremental_units * vol_cents) / u)
  return lift_cents / 100
}

/** Dollar lift for one `sales_data` point (0 when no positive incremental promo in detail). */
export function promo_lift_dollars_for_datapoint(dp: SalesDataPoint, cfg: ColumnConfig): number {
  const promo = dp.metrics?.detail?.[FORECAST_DETAIL.incremental_promo_units]
  if (promo === undefined || !Number.isFinite(promo) || promo <= 0) {
    return 0
  }
  const r = resolve_display_metrics(dp, cfg)
  return imputed_promo_lift_revenue(r.volume_dollars, r.units_sold, promo) ?? 0
}

/** Raw incremental promo units on a point (0 when missing or non-positive). */
export function promo_lift_units_for_datapoint(dp: SalesDataPoint): number {
  const promo = dp.metrics?.detail?.[FORECAST_DETAIL.incremental_promo_units]
  if (promo === undefined || !Number.isFinite(promo) || promo <= 0) {
    return 0
  }
  return promo
}

/** Secondary quantity row in period cells: units vs cases (revenue is always shown separately). */
export function effective_quantity_mode(cfg: ColumnConfig): "units" | "cases" {
  const q = cfg.quantity_mode
  if (q === "units" || q === "cases") {
    return q
  }
  // Packs view rewrites `units_sold` on the render copy, so a cell reads it as units.
  if (q === "packs") {
    return "units"
  }
  return cfg.metric_mode === "units" ? "units" : "cases"
}

/** The quantity the grid is showing, as a word. */
export function quantity_label(cfg: ColumnConfig): string {
  if (cfg.quantity_mode === "packs") return "Packs"
  return effective_quantity_mode(cfg) === "units" ? "Units" : "Cases"
}

export function metric_mode_resolvable(mode: MetricMode, availability: ForecastMeasureAvailability): boolean {
  if (!availability.has_detail) {
    return true
  }
  if (mode === "dollars") {
    return availability.consumption_dollars || availability.delivered_dollars
  }
  if (mode === "cases") {
    return availability.consumption_cases || availability.delivery_cases
  }
  if (mode === "units") {
    return (
      availability.forecast_units ||
      availability.baseline_units ||
      availability.incremental_promo_units
    )
  }
  return true
}

/** After loading a version, drop invalid slice keys and pick a valid `metric_mode`. */
export function reconcileColumnConfigWithAvailability(
  prev: ColumnConfig,
  availability: ForecastMeasureAvailability
): ColumnConfig {
  const next: ColumnConfig = { ...prev }
  if (!availability.has_detail) {
    delete next.forecast_dollar_basis
    delete next.forecast_case_basis
    delete next.forecast_unit_basis
    return next
  }

  let mode: MetricMode = (next.metric_mode ?? "dollars") as MetricMode
  if (!metric_mode_resolvable(mode, availability)) {
    mode = metric_mode_resolvable("dollars", availability)
      ? "dollars"
      : metric_mode_resolvable("cases", availability)
        ? "cases"
        : "units"
    next.metric_mode = mode
  }

  {
    const d = next.forecast_dollar_basis
    if (d === "gross_sales_delivered" && !availability.delivered_dollars && availability.consumption_dollars) {
      next.forecast_dollar_basis = "consumption_gross_sales"
    } else if (d === "consumption_gross_sales" && !availability.consumption_dollars && availability.delivered_dollars) {
      next.forecast_dollar_basis = "gross_sales_delivered"
    } else if (!d) {
      next.forecast_dollar_basis = availability.consumption_dollars
        ? "consumption_gross_sales"
        : availability.delivered_dollars
          ? "gross_sales_delivered"
          : undefined
    } else if (d === "gross_sales_delivered" && !availability.delivered_dollars) {
      next.forecast_dollar_basis = availability.consumption_dollars ? "consumption_gross_sales" : undefined
    } else if (d === "consumption_gross_sales" && !availability.consumption_dollars) {
      next.forecast_dollar_basis = availability.delivered_dollars ? "gross_sales_delivered" : undefined
    }
  }

  {
    const c = next.forecast_case_basis
    if (c === "forecasted_delivery_cases" && !availability.delivery_cases && availability.consumption_cases) {
      next.forecast_case_basis = "forecasted_consumption_cases"
    } else if (c === "forecasted_consumption_cases" && !availability.consumption_cases && availability.delivery_cases) {
      next.forecast_case_basis = "forecasted_delivery_cases"
    } else if (!c) {
      next.forecast_case_basis = availability.consumption_cases
        ? "forecasted_consumption_cases"
        : availability.delivery_cases
          ? "forecasted_delivery_cases"
          : undefined
    } else if (c === "forecasted_delivery_cases" && !availability.delivery_cases) {
      next.forecast_case_basis = availability.consumption_cases ? "forecasted_consumption_cases" : undefined
    } else if (c === "forecasted_consumption_cases" && !availability.consumption_cases) {
      next.forecast_case_basis = availability.delivery_cases ? "forecasted_delivery_cases" : undefined
    }
  }

  if (
    next.forecast_dollar_basis === "gross_sales_delivered" &&
    availability.delivery_cases
  ) {
    next.forecast_case_basis = "forecasted_delivery_cases"
  } else if (
    next.forecast_dollar_basis === "consumption_gross_sales" &&
    availability.consumption_cases
  ) {
    next.forecast_case_basis = "forecasted_consumption_cases"
  }

  {
    const u = next.forecast_unit_basis
    const pick_default =
      availability.forecast_units
        ? ("forecasted_consumption_units" as const)
        : availability.baseline_units
          ? ("seasonality_adjusted_baseline_consumption_units" as const)
          : availability.incremental_promo_units
            ? ("incremental_promotion_consumption_units" as const)
            : undefined
    const valid =
      u === "forecasted_consumption_units"
        ? availability.forecast_units
        : u === "seasonality_adjusted_baseline_consumption_units"
          ? availability.baseline_units
          : u === "incremental_promotion_consumption_units"
            ? availability.incremental_promo_units
            : false
    if (!u || !valid) {
      next.forecast_unit_basis = pick_default
    }
  }

  if (next.quantity_mode === "units" && !metric_mode_resolvable("units", availability)) {
    next.quantity_mode = "cases"
  }

  return next
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("is_delivered_detail_channel / resolve_display_metrics", () => {
    it("forces units_sold null in Delivered channel when detail exists", () => {
      const dp: SalesDataPoint = {
        period: { year: 2026, month: 1, period_key: "2026-01" },
        metrics: {
          store_count: 1,
          volume_dollars: 0,
          cases_sold: 0,
          case_cost: 0,
          detail: {
            consumption_gross_sales: 100,
            gross_sales_delivered: 200,
            forecasted_consumption_cases: 10,
            forecasted_delivery_cases: 20,
            forecasted_consumption_units: 999,
          },
        },
      }
      const cfg: ColumnConfig = {
        show_stores: true,
        show_cases: true,
        show_volume: true,
        forecast_dollar_basis: "gross_sales_delivered",
        forecast_case_basis: "forecasted_delivery_cases",
      }
      expect(is_delivered_detail_channel(cfg)).toBe(true)
      const r = resolve_display_metrics(dp, cfg)
      expect(r.volume_dollars).toBe(200)
      expect(r.cases_sold).toBe(20)
      expect(r.units_sold).toBeNull()
    })

    it("resolves units in Consumption channel from detail", () => {
      const dp: SalesDataPoint = {
        period: { year: 2026, month: 1, period_key: "2026-01" },
        metrics: {
          store_count: 1,
          volume_dollars: 0,
          cases_sold: 0,
          case_cost: 0,
          detail: { forecasted_consumption_units: 42 },
        },
      }
      const cfg: ColumnConfig = {
        show_stores: true,
        show_cases: true,
        show_volume: true,
        forecast_dollar_basis: "consumption_gross_sales",
        forecast_case_basis: "forecasted_consumption_cases",
      }
      expect(resolve_display_metrics(dp, cfg).units_sold).toBe(42)
    })
  })

  describe("imputed_promo_lift_revenue", () => {
    it("returns incremental dollars from revenue and display units × promo units", () => {
      expect(imputed_promo_lift_revenue(387_941.91, 98_735, 2704)).toBeCloseTo(10_624.35, 2)
    })
    it("returns null when display units missing or non-positive", () => {
      expect(imputed_promo_lift_revenue(100, null, 5)).toBeNull()
      expect(imputed_promo_lift_revenue(100, 0, 5)).toBeNull()
    })
  })

  describe("effective_quantity_mode", () => {
    it("prefers quantity_mode when set", () => {
      expect(
        effective_quantity_mode({
          show_stores: true,
          show_cases: true,
          show_volume: true,
          quantity_mode: "units",
          metric_mode: "dollars",
        }),
      ).toBe("units")
    })

    it("reads packs as units for the cell code and says Packs on the label", () => {
      const packs = { show_stores: true, show_cases: true, show_volume: true, quantity_mode: "packs" as const }
      expect(effective_quantity_mode(packs)).toBe("units")
      expect(quantity_label(packs)).toBe("Packs")
      expect(quantity_label({ ...packs, quantity_mode: "units" })).toBe("Units")
      expect(quantity_label({ ...packs, quantity_mode: "cases" })).toBe("Cases")
    })

    it("falls back from metric_mode when quantity_mode omitted", () => {
      expect(
        effective_quantity_mode({
          show_stores: true,
          show_cases: true,
          show_volume: true,
          metric_mode: "units",
        }),
      ).toBe("units")
      expect(
        effective_quantity_mode({
          show_stores: true,
          show_cases: true,
          show_volume: true,
          metric_mode: "dollars",
        }),
      ).toBe("cases")
    })
  })

  describe("scan_forecast_measure_availability / reconcileColumnConfigWithAvailability", () => {
    it("detects detail keys on sku leaves", () => {
      const nodes = [
        {
          id: "pl",
          level: "product_line",
          name: "PL",
          children: [
            {
              id: "sku",
              level: "sku",
              name: "SKU",
              sales_data: [
                {
                  period: { period_key: "2026-01" },
                  metrics: {
                    store_count: 1,
                    volume_dollars: 10,
                    cases_sold: 2,
                    case_cost: 0,
                    detail: {
                      consumption_gross_sales: 100,
                      gross_sales_delivered: 5,
                      forecasted_consumption_cases: 3,
                      forecasted_delivery_cases: 1,
                    },
                  },
                },
              ],
            },
          ],
        },
      ] as unknown as SalesHierarchyNode[]
      const a = scan_forecast_measure_availability(nodes)
      expect(a.has_detail).toBe(true)
      expect(a.consumption_dollars).toBe(true)
      expect(a.delivered_dollars).toBe(true)
    })

    it("falls back metric_mode when dollars slice missing", () => {
      const prev: ColumnConfig = {
        show_stores: true,
        show_cases: true,
        show_volume: true,
        metric_mode: "dollars",
      }
      const avail: ForecastMeasureAvailability = {
        has_detail: true,
        consumption_dollars: false,
        delivered_dollars: false,
        consumption_cases: true,
        delivery_cases: true,
        forecast_units: false,
        baseline_units: false,
        incremental_promo_units: false,
        promotion_lift: false,
      }
      const next = reconcileColumnConfigWithAvailability(prev, avail)
      expect(next.metric_mode).toBe("cases")
      expect(next.forecast_case_basis).toBe("forecasted_consumption_cases")
    })

    it("pairs delivered dollars with delivery cases when both exist", () => {
      const prev: ColumnConfig = {
        show_stores: true,
        show_cases: true,
        show_volume: true,
        metric_mode: "dollars",
        forecast_dollar_basis: "gross_sales_delivered",
        forecast_case_basis: "forecasted_consumption_cases",
      }
      const avail: ForecastMeasureAvailability = {
        has_detail: true,
        consumption_dollars: true,
        delivered_dollars: true,
        consumption_cases: true,
        delivery_cases: true,
        forecast_units: true,
        baseline_units: false,
        incremental_promo_units: false,
        promotion_lift: false,
      }
      const next = reconcileColumnConfigWithAvailability(prev, avail)
      expect(next.forecast_dollar_basis).toBe("gross_sales_delivered")
      expect(next.forecast_case_basis).toBe("forecasted_delivery_cases")
    })
  })
}
